import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, effect, input, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { EnpsControlComponent } from '@common/controls/enps-control/enps-control';
import { selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { optionContainsTerm } from '@src/suite/common/utils/util';
import { Observable } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

export type DeskItem = { id: string; title: string; selectable: boolean; initials?: string };

@Component({
    selector: 'st-desk',
    templateUrl: './desk.html',
    styleUrls: ['./desk.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'st-desk',
    },
})
export class DeskComponent extends EnpsControlComponent<Array<string>> {
    itemList = input<DeskItem[]>([]);
    maxItemsToShow = input(2);
    valueSignal = input<string[]>([], { alias: 'value' });
    internalValue = signal<string[]>([]);
    isListOpen = signal(false);

    overlay = viewChild(CdkConnectedOverlay);
    selectionList = viewChild<MatSelectionList>(MatSelectionList);

    deskFilter = new FormControl('');
    filteredOptions: Observable<DeskItem[]>;

    extraItemsCount = computed(() => this.internalValue().length - this.maxItemsToShow());
    shouldShowExtraItems = computed(() => this.extraItemsCount() > 0);

    indexes = computed(() => {
        const length = this.shouldShowExtraItems() ? this.maxItemsToShow() : this.internalValue().length;
        return Array.from({ length }, (_, i) => i);
    });

    tooltipItems = computed(() => {
        const items = this.internalValue()?.slice(this.indexes().length);
        if (items.length > 0) {
            return this.itemList()
                .filter(option => items.includes(option.id))
                .map(item => item);
        }
    });

    searchLabel = computed(() => `${this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebSearchLabel))()}...`);
    isCompactView = computed(() => this.maxItemsToShow() === 0);
    hasButtonLabel = computed(() => this.internalValue()?.length === 0 && !this.isCompactView());
    addButtonLabel = computed(() => (this.hasButtonLabel() ? `${this.label()}` : ''));

    constructor() {
        super();
        effect(
            () => {
                this.internalValue.set([...(this.valueSignal() || [])]);
            },
            { allowSignalWrites: true }
        );
    }

    getItemLabel(item: string) {
        const existingItem = this.itemList().find(option => option.id?.toLowerCase() === item?.toLowerCase())?.title;
        if (existingItem) return existingItem;
        return item;
    }

    valueChanged() {
        if (this.isValueChanged()) {
            this.isValueChanged.set(false);
            this.changed.emit(this.internalValue());
        }
        this.deskFilter.setValue('');
    }

    private _filter(value: string): { id: string; title: string; selectable: boolean; initials?: string }[] {
        return this.itemList().filter(option => optionContainsTerm(option.id, value) || optionContainsTerm(option.title, value));
    }

    remove(item: string): void {
        const index = this.internalValue().indexOf(item);

        if (index >= 0) {
            this.internalValue.update(values => {
                values?.splice(index, 1);
                return [...(values || [])];
            });
        }
        this.isValueChanged.set(true);
    }

    selected(event: MatSelectionListChange): void {
        const selectedDesk = this.internalValue().find(option => option === event.options[0].value);
        if (selectedDesk) {
            this.internalValue.update(values => {
                values?.splice(values?.indexOf(selectedDesk), 1);
                return [...(values || [])];
            });
        } else {
            // this.internalValue.set([...(this.internalValue() || []), event.options[0].value]);
            this.internalValue.update(values => {
                return [...(values || []), event.options[0].value];
            });
        }

        this.searchBox().nativeElement.value = '';
        this.deskFilter.setValue('');
        this.isValueChanged.set(true);
    }

    onMenuOpened(): void {
        super.onMenuOpened();
        this.filteredOptions = this.deskFilter.valueChanges.pipe(
            startWith(''),
            map(value => this._filter(value || '')),
            takeUntil(this.destroyed$)
        );
    }

    showItemTooltip() {
        this.isListOpen.set(true);
    }

    onMouseOut(event: MouseEvent) {
        if (!(event.relatedTarget as HTMLElement)?.classList?.contains('st-desk-value__tooltip__content')) {
            this.isListOpen.set(false);
        }
    }

    panelAttached() {
        const overlayElement = this.overlay()?.overlayRef?.overlayElement;
        if (overlayElement) {
            overlayElement.addEventListener('mouseleave', () => {
                this.isListOpen.set(false);
            });
        }
    }

    onUpArrowPress() {
        this.selectionList()?.options?.last?.focus();
    }

    onDownArrowPress() {
        this.selectionList()?.options?.first?.focus();
    }
}
